import { Injectable } from '@angular/core';
import { AbstractCRUDService } from '@campaign-portal/components-library';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { UsersAndRolesService } from '@helpers/api/users-and-roles.service';
import {
	UsersCreateRequest,
	UsersCreateResponse,
	UsersDeleteRequest,
	UsersDeleteResponse,
	UsersReadRequest,
	UsersUpdateRequest,
	UsersUpdateResponse,
} from '@helpers/types/user-and-roles';
import { User } from '@helpers/types/models';
import { ExportService } from '@helpers/api/export.service';
import { UsersFieldsService } from './users.fields.service';
import { Filter, RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { exist } from '@campaign-portal/namespace/common/id';
import { ReadResponse } from '@campaign-portal/namespace/common/implementations';
import { HlrExtendableRefBookService } from '@helpers/providers/hlr-extendable-ref-book.service';

@Injectable({
	providedIn: 'root',
})
export class UsersService extends HlrExtendableRefBookService<User<exist>> implements AbstractCRUDService {
	override readonly loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	readonly entity = '';
	readonly title = '';
	readonly create = this.update;

	constructor(
		private readonly usersAndRoles: UsersAndRolesService,
		private readonly fields: UsersFieldsService,
		private readonly exportService: ExportService
	) {
		super();
	}

	override load(): Observable<ReadResponse<User<number>[]>> {
		return this.usersAndRoles.loader<ReadResponse<User<number>[]>>('Users.Read', {}, this.loading$).pipe(
			map((resp) => {
				return this.process(resp);
			})
		);
	}

	override process(resp: ReadResponse<User<number>[]>): ReadResponse<User<number>[]> {
		const data = Array.isArray(resp.Data) ? resp.Data : [];
		this.list$.next(data);
		if (data.length > 0) {
			this.map$.next(
				data.reduce((result, item) => {
					result.set(item.ID, item);
					return result;
				}, new Map())
			);
		} else {
			this.map$.next(new Map());
		}
		return resp;
	}

	read(params: RPCRequestParams): Observable<ReadResponse<User[]>> {
		const parsedParams = this.prepareParams(params);
		return this.usersAndRoles.loader<ReadResponse<User[]>>('Users.Read', parsedParams, this.loading$);
	}

	update(params: UsersCreateRequest | UsersUpdateRequest): Observable<UsersUpdateResponse | UsersCreateResponse> {
		const method = params.ID ? 'Users.Update' : 'Users.Create';
		return this.usersAndRoles.loader<UsersUpdateResponse | UsersCreateResponse>(method, params, this.loading$);
	}

	delete(params: UsersDeleteRequest): Observable<UsersDeleteResponse> {
		return this.usersAndRoles.loader<UsersDeleteResponse>('Users.Delete', params, this.loading$);
	}

	export(total: number): Observable<void> {
		return this.exportService.export(
			this.usersAndRoles.userAndRolesApi.url,
			'Users.Read',
			{
				Paging: {
					Skip: 0,
					Take: total,
				},
			},
			this.fields.headers,
			'users'
		);
	}

	// resetCode(params: UsersResetCodeRequest): Observable<any> {
	// 	return this.usersAndRoles.loader<UsersResetCodeRequest>('TwoFactor.Reced', params, this.loading$);
	// }

	private prepareParams(params: RPCRequestParams): UsersReadRequest {
		return {
			Sorting: [{ Field: 'ID', Dir: 'desc' }],
			Paging: { Skip: params.Paging?.Skip ?? 0, Take: params.Paging?.Take ?? 25 },
			Filters: params.Filters
				? params.Filters.map((filter) => {
						return this.parseFilterField(filter);
					})
				: [],
		};
	}

	private parseFilterField(filter: Filter): { Type: string; Value: string } {
		const variable = filter.Field;
		const value = filter.Value;
		switch (variable) {
			case 'Login':
				return { Type: 'login', Value: value as string };
			case 'Email':
				return { Type: 'email', Value: value as string };
			case 'Details':
				return { Type: 'name', Value: value as string };

			default:
				return { Type: variable, Value: value + '' };
		}
	}
}
